import {
  Box,
  Fab,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Zoom,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { DateTime } from "luxon";
import { useUsers } from "../../api/use-user";
import { DeleteUser } from "./delete-user";
import { UserContext } from "../../contexts/user-context";
import AddIcon from "@mui/icons-material/Add";
import prettyBytes from "pretty-bytes";

export const Users = () => {
  const { t } = useTranslation(["user", "common"]);
  const [dataPerPage, setDataPerPage] = useState({});
  const [page, setPage] = useState(0);
  const pageSize = 60;
  const [{ data, loading }] = useUsers(pageSize, page * pageSize);
  const { user } = useContext(UserContext);

  useEffect(() => {
    setDataPerPage((d) => ({ ...d, [page]: data }));
  }, [page, data]);

  const [userToDelete, setUserToDelete] = useState(null);

  const TableCellLinkWrapper = ({ id, children }) => {
    return (
      <Box
        component={RouterLink}
        to={`/users/${id}`}
        sx={{
          textDecoration: "none",
          color: "text.primary",
          cursor: "pointer",
          display: "block",
          p: 2,
        }}
      >
        {children}
      </Box>
    );
  };
  const [rowsPerPage, setRowsPerPage] = useState(30);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", mb: 3 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {t("users")}
        </Typography>
        <Fab
          component={RouterLink}
          to={`/users/new`}
          variant="extended"
          color="primary"
        >
          <AddIcon sx={{ mr: 1 }} />
          {t("newUser")}
        </Fab>
      </Box>
      <Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("id")}</TableCell>
                <TableCell>{t("name")}</TableCell>
                <TableCell>{t("email")}</TableCell>
                <TableCell>{t("role")}</TableCell>
                <TableCell>{t("consumedStorage")}</TableCell>
                <TableCell>{t("projectCount")}</TableCell>
                <TableCell>{t("remainingProjects")}</TableCell>
                <TableCell>{t("createdAt")}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(dataPerPage[page] ?? []).map((u) => (
                <TableRow
                  key={u.id}
                  sx={{
                    "&:hover td": { backgroundColor: "#303030" },
                    "&:last-child td, &:last-child th": { border: 0 },
                    "& td": { whiteSpace: "nowrap", p: 0 },
                  }}
                >
                  <TableCell>
                    <TableCellLinkWrapper id={u.id}>
                      {u.id}
                    </TableCellLinkWrapper>
                  </TableCell>
                  <TableCell>
                    <TableCellLinkWrapper id={u.id}>
                      {u.name}
                    </TableCellLinkWrapper>
                  </TableCell>
                  <TableCell>
                    <TableCellLinkWrapper id={u.id}>
                      {u.email}
                    </TableCellLinkWrapper>
                  </TableCell>
                  <TableCell>
                    <TableCellLinkWrapper id={u.id}>
                      {u.role}
                    </TableCellLinkWrapper>
                  </TableCell>
                  <TableCell>
                    <TableCellLinkWrapper id={u.id}>
                      {prettyBytes(u.consumedStorageMb * 1024 * 1024)}
                    </TableCellLinkWrapper>
                  </TableCell>
                  <TableCell>
                    <TableCellLinkWrapper id={u.id}>
                      {u.projectCount}
                    </TableCellLinkWrapper>
                  </TableCell>
                  <TableCell>
                    <TableCellLinkWrapper id={u.id}>
                      {u.remainingProjects}
                    </TableCellLinkWrapper>
                  </TableCell>
                  <TableCell>
                    <TableCellLinkWrapper id={u.id}>
                      {DateTime.fromISO(u.createdAt).toFormat("yyyy.MM.dd")}
                    </TableCellLinkWrapper>
                  </TableCell>
                  <TableCell>
                    {u.id !== user?.id && (
                      <Link
                        sx={{
                          color: "primary.contrastText",
                          textDecorationColor: (t) =>
                            t.palette.primary.contrastText,
                          cursor: "pointer",
                        }}
                        onClick={() => setUserToDelete(u)}
                      >
                        {t("delete")}
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[30, 50, 100]}
          component="div"
          count={-1}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      <DeleteUser
        open={!!userToDelete}
        user={userToDelete}
        onClose={() => setUserToDelete(null)}
      />
    </Paper>
  );
};
